// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as TimelineActions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import { AddToTimeGroupRequestSchema, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type AddToTimeGroupRpc = (request: Init<typeof AddToTimeGroupRequestSchema>) => Observable<TimeGroup>

export const addToTimeGroup = (addToTimeGroupRpc: AddToTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.addToTimeGroup.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(addToTimeGroupRpc),
          map(timeGroup => timeGroup),
          mergeMap(timeGroup =>
            concat(
              of(TimelineSlice.upsertTimeGroup(timeGroup)),
              of(TimelineSlice.setTimeRowsGroupId({
                timeRowIds: action.payload.timeRowIds ?? [],
                timeGroupId: timeGroup.id,
              })),
              of(TimelineActions.generateTimesheetFieldConfigs([timeGroup])),
              of(TimelineAdditionalUiSlice.addBlinkTimeRowsColumnTimeGroupIds(timeGroup.id)),
              of(TimelineSlice.removeActiveTimeRowIds({ timeRowIds: action.payload.timeRowIds ?? [], })),
            )
          ),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_to_group_not_found", message: "Timesheet or time row(s) not found." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_add_to_group_permission_denied", message: "Failed to add time row(s) to the timesheet. You do not have permission to update the timesheet." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_to_group_failed_precondition", message: "Timesheet has already been posted or time row(s) have already been attached to the timesheet." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_add_to_group_failed", message: "Failed to add time row(s) to the timesheet. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const addToTimeGroupRpc = (request: Init<typeof AddToTimeGroupRequestSchema>): Observable<TimeGroup> =>
  executorMutation(() => timelineService.addToTimeGroup(create(AddToTimeGroupRequestSchema, request)))

export default addToTimeGroup(addToTimeGroupRpc)
